import useMountEffect from '@restart/hooks/useMountEffect';
import classNames from 'classnames/bind';
import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import CusSurvey from './CusSurvey';
import styles from './index.module.scss';
import NavReturnToLaunch from './NavReturnToLaunch';
import NavTakeoff from './NavTakeoff';
import NavWaypoint from './NavWaypoint';

import actions from '@/actions';
import AccordionCaret from '@/components/ui/AccordionCaret';
import Select from '@/components/ui/kit/Select';
import Marker from '@/components/ui/map/Marker';
import MorePanel from '@/components/ui/MorePanel';
import EventEmitter from '@/libs/EventEmitter';
import { NotifierService as notifier } from '@/libs/Notifier';

const cx = classNames.bind(styles);

const Command = ({ index, data: command }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const markers = useSelector((state) => state.editor.markers);
  const [isOpenBody, setIsOpenBody] = useState(true);

  // Waypoint 경우 유형 변경 가능
  const isChangeableType = useMemo(() => {
    return ['navWaypoint'].includes(command.type);
  }, [command.type]);

  const markerLabel = useMemo(() => {
    const { id, type } = command;

    if (markers[id]) {
      return markers[id].label;
    } else if (type === 'cusSurvey') {
      return markers[`${id}/survey/start`]?.label ?? null;
    }

    return null;
  }, [markers]);

  const Icon = useMemo(() => {
    // prettier-ignore
    switch (command.type) {
      // Use default marker icon below for these types.
      case 'navReturnToLaunch':
      case 'navTakeoff':
      case 'navWaypoint':
      case 'cusSurvey':
      default:
        return;
    }
  }, [command.type]);

  const specs = useMemo(() => {
    return store.getState().mission.json.specs;
  }, []);

  useMountEffect(() => {
    const toggle = (nextIsOpenBody) => {
      if (nextIsOpenBody === undefined) {
        setIsOpenBody((prev) => !prev);
      } else {
        setIsOpenBody(nextIsOpenBody);
      }
    };

    const subscribeToken = EventEmitter.subscribe(`command/${command.id}/toggle`, toggle);

    return () => {
      EventEmitter.unsubscribe(subscribeToken);
    };
  });

  const changeType = (e) => {
    dispatch(actions.mission.mavlink.changeCommandType(index, e.target.value));
  };

  const toggleBody = () => {
    setIsOpenBody(!isOpenBody);
  };

  const remove = () => {
    if (store.getState().map3d.isShow) {
      notifier.error('Commands cannot be deleted in 3D map.');
      return;
    }

    dispatch(actions.mission.removeCommand(index));
  };

  const moveUp = () => {
    if (index < 2) {
      notifier.error('Cannot be moved up anymore.');
      return;
    }

    dispatch(actions.mission.shiftCommand(index, -1));
  };

  const moveDown = () => {
    if (index === store.getState().mission.json.commands.length - 1) {
      notifier.error('Cannot be moved down anymore.');
      return;
    }

    dispatch(actions.mission.shiftCommand(index, 1));
  };

  const prepareToMoveTo = () => {
    // Takeoff 외 미션이 하나만 있는 경우
    if (store.getState().mission.json.commands.length === 2) {
      notifier.error('No available positions to move.');
      return;
    }

    dispatch(actions.editor.setMoveFromIndex(index));
    dispatch(actions.editor.changeMode(null));
  };

  return (
    <div className={cx('container')}>
      <div className={cx('header')} onClick={toggleBody}>
        <div className={cx('left')}>
          {markerLabel && (
            <div className={cx('marker')}>
              <Marker data={{ label: markerLabel }} />
            </div>
          )}
          {Icon && (
            <div className={cx('icon')}>
              <Icon size={20} color="white" />
            </div>
          )}
          {
            // prettier-ignore
            <Select
              value={command.type}
              disabled={!isChangeableType || store.getState().editor.moveFromIndex !== null}
              className={cx('type')}
              onChange={changeType}>
                <option value="navReturnToLaunch" hidden>Return To Launch</option>
                <option value="cusSurvey" hidden>Survey</option>
                <option value="navTakeoff" hidden>Takeoff</option>
                <option value="navWaypoint">Waypoint</option>
            </Select>
          }
        </div>
        <div className={cx('right')}>
          {command.type !== 'navTakeoff' && (
            <MorePanel
              data={[
                { name: 'Remove', callback: remove },
                { name: 'Move Up', callback: moveUp },
                { name: 'Move Down', callback: moveDown },
                { name: 'Move To', callback: prepareToMoveTo },
              ]}
            />
          )}
          <AccordionCaret up={isOpenBody} size={32} />
        </div>
      </div>
      <div className={cx(['body', { open: isOpenBody }])}>
        {
          // prettier-ignore
          <div className={cx('inner')}>
            {command.type === 'navReturnToLaunch' && <NavReturnToLaunch />}
            {command.type === 'navTakeoff' && <NavTakeoff index={index} data={command} />}
            {command.type === 'navWaypoint' && <NavWaypoint index={index} data={command} />}
            {command.type === 'cusSurvey' && <CusSurvey index={index} data={command} />}
          </div>
        }
      </div>
    </div>
  );
};

export default Command;
